import {useEffect, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {useSuiContext} from '@s-ui/react-context'
import PrimitiveTypography, {
  primitiveTypographyDesign,
  primitiveTypographyFontWeight
} from '@s-ui/react-primitive-typography'
import IconAngleLeft from '@adv-mt/mt-svg-icons/lib/AngleLeft'
import IconAngleRight from '@adv-mt/mt-svg-icons/lib/AngleRight'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {getTrackingAdImpressions} from '../../tracking.js'
import NetAdCard from '../adCard/index.js'

const BASE_CLASS = 'mt-NetAdCarousel'

const NetAdCarousel = ({items = [], chatId, isWide = false, onAdClick, onFavoriteChange, setIsVisibleLoginModal}) => {
  const {domain, i18n} = useSuiContext()
  const {track} = useAnalyticsTrack()
  const listRef = useRef(null)
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)

  const config = domain.get('config')
  const tracking = config.get('tracking')
  const {LIST_TYPES: {NET_ASSISTANT_CAROUSEL} = {}} = tracking || {}

  useEffect(() => {
    if (!items.length) return

    const trackImpressions = async () => {
      const trackingData = await domain.get('get_advertisements_tracking_use_case').execute({
        ads: items.map(({price: _price, ...rest}) => ({...rest, price: _price.amount})),
        adsTypeName: 'ad_impression',
        listType: NET_ASSISTANT_CAROUSEL
      })
      track(getTrackingAdImpressions(tracking, chatId, trackingData.products))
    }

    trackImpressions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateArrows = () => {
    const list = listRef.current
    if (!list) return
    setIsAtStart(list.scrollLeft <= 0)
    setIsAtEnd(list.scrollLeft + list.clientWidth >= list.scrollWidth)
  }

  const scrollBy = direction => {
    const list = listRef.current
    if (!list) return
    const cardWidth = list.firstChild?.offsetWidth || 0
    list.scrollBy({left: direction * cardWidth, behavior: 'smooth'})
  }

  useEffect(() => {
    const list = listRef.current
    if (!list) return
    updateArrows()
    list.addEventListener('scroll', updateArrows)
    return () => list.removeEventListener('scroll', updateArrows)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!items.length) return null

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-heading`}>
        <PrimitiveTypography
          as="h2"
          design={primitiveTypographyDesign.HEADLINE_2}
          fontWeight={primitiveTypographyFontWeight.SEMI_BOLD}
        >
          {i18n.t('NET.SEARCH_RESULTS.FEATURED_ADS')}
        </PrimitiveTypography>
        <div className={`${BASE_CLASS}-arrows`}>
          <AtomButton
            design={atomButtonDesigns.OUTLINE}
            shape={atomButtonShapes.CIRCULAR}
            size={atomButtonSizes.SMALL}
            disabled={isAtStart}
            onClick={() => scrollBy(-1)}
            aria-label={i18n.t('NET.SEARCH_RESULTS.CAROUSEL_PREV')}
          >
            <IconAngleLeft aria-hidden role="presentation" />
          </AtomButton>
          <AtomButton
            design={atomButtonDesigns.OUTLINE}
            shape={atomButtonShapes.CIRCULAR}
            size={atomButtonSizes.SMALL}
            disabled={isAtEnd}
            onClick={() => scrollBy(1)}
            aria-label={i18n.t('NET.SEARCH_RESULTS.CAROUSEL_NEXT')}
          >
            <IconAngleRight aria-hidden role="presentation" />
          </AtomButton>
        </div>
      </div>
      <div className={`${BASE_CLASS}-list`} ref={listRef}>
        {items.map(ad => (
          <div key={ad.id} className={cx(`${BASE_CLASS}-item`, {[`${BASE_CLASS}-item--wide`]: isWide})}>
            <NetAdCard
              ad={ad}
              onAdClick={onAdClick}
              onFavoriteChange={onFavoriteChange}
              setIsVisibleLoginModal={setIsVisibleLoginModal}
              extendedTrackingData={{listType: NET_ASSISTANT_CAROUSEL, hit_information: chatId}}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

NetAdCarousel.displayName = 'NetAdCarousel'

NetAdCarousel.propTypes = {
  items: PropTypes.array,
  chatId: PropTypes.string,
  isWide: PropTypes.bool,
  onAdClick: PropTypes.func,
  onFavoriteChange: PropTypes.func,
  setIsVisibleLoginModal: PropTypes.func
}

export default NetAdCarousel
